/**
 * MarkdownStreamer — accumulates streaming text and converts inline markdown
 * to HTML before inserting into TipTap.
 *
 * Handles headings (#-######), bold (**), italic (*), and strikethrough (~~) markup.
 * Holds back incomplete tokens at chunk boundaries so split delimiters
 * across streaming chunks are handled correctly.
 *
 * Uses a lightweight regex-based converter (convertInlineMarkdown) to
 * transform markdown syntax into HTML tags (<h1>-<h6>, <strong>, <em>, <s>).
 */

/**
 * Apply inline formatting regex replacements to text.
 * Converts **bold**, __bold__, *italic*, _italic_, and ~~strikethrough~~
 * to their HTML equivalents. Bold runs before italic so ** is consumed first.
 */
function applyInlineFormatting(text) {
  let result = text;
  result = result.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  result = result.replace(/__(.+?)__/g, "<strong>$1</strong>");
  result = result.replace(/\*(.+?)\*/g, "<em>$1</em>");
  result = result.replace(/(?<!\w)_(.+?)_(?!\w)/g, "<em>$1</em>");
  result = result.replace(/~~(.+?)~~/g, "<s>$1</s>");
  return result;
}

/**
 * Convert inline markdown to HTML.
 * Handles # headings (h1-h6), **bold**, *italic*, and ~~strikethrough~~.
 * Headings are detected at line start and wrapped in block-level <h1>-<h6> tags.
 * Does NOT wrap in <p> tags.
 */
function convertInlineMarkdown(text) {
  if (!text) return text;

  let result = text;

  // Heading: # text through ###### text
  const headingMatch = result.match(/^(#{1,6})\s+(.+)$/);
  if (headingMatch) {
    const level = headingMatch[1].length;
    const inlineContent = applyInlineFormatting(headingMatch[2]);
    return `<h${level}>${inlineContent}</h${level}>`;
  }

  return applyInlineFormatting(result);
}

/**
 * Find the safe split point in accumulated text.
 * Returns the index up to which we can safely convert markdown.
 * Everything after that index might be part of an incomplete delimiter.
 *
 * We look for trailing characters that could be the start of a markdown
 * delimiter: *, _, ~, #
 *
 * We also check for unmatched opening delimiters (e.g., "hello **wor"
 * where ** has opened but not closed).
 */
function findSafeSplitPoint(text) {
  if (!text) return 0;

  // First, check for trailing delimiter characters that could be the
  // start of a new delimiter sequence
  let trailingStart = text.length;
  while (trailingStart > 0 && "*_~#".includes(text[trailingStart - 1])) {
    trailingStart--;
  }

  // If the entire string is delimiter characters, hold it all back
  if (trailingStart === 0) return 0;

  // Check for heading prefix at start of text: #{1,6} followed by space.
  // If present, the whole text is a potential heading line. We must keep
  // it together — either emit all of it or none — so that
  // convertInlineMarkdown can detect the heading pattern.
  const headingStart = text.match(/^#{1,6}\s/);
  if (headingStart) {
    // If the text is just the prefix with no content, hold everything back
    if (text.match(/^#{1,6}\s*$/)) return 0;
    // Otherwise, check if inline markdown within the heading content is
    // complete. If trailingStart < text.length, there are trailing
    // delimiters — hold everything back.
    if (trailingStart < text.length) return 0;
    // Check for unmatched inline delimiters in the heading content
    const headingContent = text.slice(headingStart[0].length);
    // Unmatched **
    if (headingContent.split("**").length % 2 === 0) return 0;
    // Unmatched __
    if (headingContent.split("__").length % 2 === 0) return 0;
    // Unmatched ~~
    if (headingContent.split("~~").length % 2 === 0) return 0;
    // Unmatched single *
    const noDoubleStars = headingContent.replace(/\*\*/g, "");
    if ((noDoubleStars.match(/\*/g) || []).length % 2 === 1) return 0;
    // Unmatched single _
    if ((headingContent.match(/(?<!\w)_|_(?!\w)/g) || []).length % 2 === 1) return 0;
    // All inline delimiters are matched — safe to emit the full heading
    return text.length;
  }

  // Now check the text up to trailingStart for unmatched opening delimiters
  const safeText = text.slice(0, trailingStart);

  // Check for unmatched ** (bold)
  const boldParts = safeText.split("**");
  if (boldParts.length % 2 === 0) {
    // Odd number of ** delimiters means one is unclosed
    // Find the position of the last unmatched **
    const lastUnmatched = safeText.lastIndexOf("**");
    if (lastUnmatched >= 0) {
      return lastUnmatched;
    }
  }

  // Check for unmatched __ (bold)
  const underBoldParts = safeText.split("__");
  if (underBoldParts.length % 2 === 0) {
    const lastUnmatched = safeText.lastIndexOf("__");
    if (lastUnmatched >= 0) {
      return Math.min(trailingStart, lastUnmatched);
    }
  }

  // Check for unmatched ~~ (strikethrough)
  const strikeParts = safeText.split("~~");
  if (strikeParts.length % 2 === 0) {
    const lastUnmatched = safeText.lastIndexOf("~~");
    if (lastUnmatched >= 0) {
      return Math.min(trailingStart, lastUnmatched);
    }
  }

  // Check for unmatched single * (italic)
  // We need to be careful: count * that are NOT part of **
  const textForSingleStar = safeText.replace(/\*\*/g, "");
  const singleStarCount = (textForSingleStar.match(/\*/g) || []).length;
  if (singleStarCount % 2 === 1) {
    // Find the last unmatched single * in the original text
    // Walk backward to find a * that is not part of **
    for (let i = safeText.length - 1; i >= 0; i--) {
      if (safeText[i] === "*") {
        // Check it is not part of **
        const prevChar = i > 0 ? safeText[i - 1] : "";
        const nextChar = i < safeText.length - 1 ? safeText[i + 1] : "";
        if (prevChar !== "*" && nextChar !== "*") {
          return Math.min(trailingStart, i);
        }
      }
    }
  }

  // Check for unmatched single _ (italic)
  const singleUnderCount = (safeText.match(/(?<!\w)_|_(?!\w)/g) || []).length;
  if (singleUnderCount % 2 === 1) {
    // Find last unmatched _ that is not part of __
    for (let i = safeText.length - 1; i >= 0; i--) {
      if (safeText[i] === "_") {
        const prevChar = i > 0 ? safeText[i - 1] : "";
        const nextChar = i < safeText.length - 1 ? safeText[i + 1] : "";
        if (prevChar !== "_" && nextChar !== "_") {
          return Math.min(trailingStart, i);
        }
      }
    }
  }

  return trailingStart;
}

export class MarkdownStreamer {
  constructor() {
    this.pending = "";
  }

  /**
   * Feed a streaming chunk. Returns HTML safe to insert now,
   * or empty string if everything is held back.
   */
  write(chunk) {
    if (!chunk) return "";

    this.pending += chunk;

    const splitPoint = findSafeSplitPoint(this.pending);

    if (splitPoint === 0) {
      // Nothing safe to emit yet
      return "";
    }

    const safe = this.pending.slice(0, splitPoint);
    this.pending = this.pending.slice(splitPoint);

    return convertInlineMarkdown(safe);
  }

  /**
   * Flush all remaining text (call on stream end).
   * Converts whatever is left, best-effort.
   */
  flush() {
    if (!this.pending) return "";

    const html = convertInlineMarkdown(this.pending);
    this.pending = "";
    return html;
  }

  /**
   * Reset internal state for reuse.
   */
  reset() {
    this.pending = "";
  }
}

// Export for testing
export { applyInlineFormatting, convertInlineMarkdown, findSafeSplitPoint };
